import type { MenuItem } from '../Menu';

/**
 * What EVERY profile's settings cog offers, before a page adds anything of its own.
 *
 * **The three a record always has** — correct it, look at what has happened to it, take it
 * down — and the reason they are built in rather than passed: `ProfileHeader` took a free-form
 * `actions` node, so 25 profile pages each hand-rolled the same three entries. That is the
 * `onCreate` defect one layer up: *a slot that takes a `ReactNode` where a callback would do*.
 * Twenty-five copies means twenty-five chances for one to use a different word, a different
 * order, or no permission gate at all.
 *
 * **Order is fixed and DELETE IS LAST**, matching `<RowActionMenu>` — a destructive entry does
 * not sit where a reader's hand lands on the way to something else. A page's own entries go in
 * the middle, so the two it is most likely to want are always in the same two places.
 *
 * **Each is dropped when the page does not supply its handler**, and gated on the key it is
 * given. `<Menu>` filters the permission itself, so a reader without `<module>.delete` sees a
 * menu with no Delete rather than a disabled one — hide, don't disable. A `disabledReason` is
 * for STATE, which is why Edit and Delete take one and History Logs does not: a record's
 * history is readable whatever state the record is in.
 */
export interface ProfileSettingsActions {
  onEdit?: () => void;
  /** Usually `<module>.update`. */
  editPermission?: string;
  /** STATE, not permission — a published release, a locked record. */
  editDisabledReason?: string | null;

  /** The module's own History Logs for this one record. */
  onHistory?: () => void;
  /**
   * Defaults to `audit-logs.read`, which is what every page that hand-rolled this entry was
   * already passing. Override only where a module gates its own trail differently.
   */
  historyPermission?: string;

  onDelete?: () => void;
  /** Usually `<module>.delete`. */
  deletePermission?: string;
  deleteDisabledReason?: string | null;

  /** The page's own entries, between History Logs and Delete. */
  menuItems?: MenuItem[];
}

/**
 * The cog's entries, in the one order every profile uses.
 *
 * Returns an empty array when a page supplies nothing, and the header then draws no cog —
 * a control that opens an empty menu says the record has actions and then shows none.
 */
export function profileSettingsItems(
  actions: ProfileSettingsActions,
  icons: { edit: MenuItem['icon']; history: MenuItem['icon']; remove: MenuItem['icon'] },
): MenuItem[] {
  const items: MenuItem[] = [];

  if (actions.onEdit) {
    items.push({
      key: 'edit',
      label: 'Edit',
      icon: icons.edit,
      permission: actions.editPermission,
      disabledReason: actions.editDisabledReason ?? null,
      onClick: actions.onEdit,
    });
  }

  if (actions.onHistory) {
    items.push({
      key: 'history',
      label: 'History Logs',
      icon: icons.history,
      permission: actions.historyPermission ?? 'audit-logs.read',
      onClick: actions.onHistory,
    });
  }

  for (const item of actions.menuItems ?? []) {
    items.push(item);
  }

  if (actions.onDelete) {
    items.push({
      key: 'delete',
      label: 'Delete',
      icon: icons.remove,
      // A rule above it, so the destructive entry is visibly apart from what precedes it.
      divider: items.length > 0,
      destructive: true,
      permission: actions.deletePermission,
      disabledReason: actions.deleteDisabledReason ?? null,
      onClick: actions.onDelete,
    });
  }

  return items;
}
